import { create } from 'zustand';

/**
 * 20260928 - THE CANVAS OVERLAY'S BRIDGE STATE: what Unity last asked the `?page=canvas-overlay`
 * page to show over the live canvas, and the two pieces of context it shows it with.
 *
 * A LEAF STORE, on purpose, like platform/foldStateStore.ts and platform/inputCapabilitiesStore.ts:
 * bridge/features/canvasOverlay.ts writes it from the dispatch table, and a uiStore edge from there
 * would drag embeddedBridge into every graph that reads this. It imports zustand and nothing else.
 *
 * It is Unity's side of the conversation only. What the PAGE has open (the menu, a sheet, the
 * Preferences modal) is component state in CanvasOverlayPage: the page switches views in place from
 * the menu without a round trip, so `view` here is "the last view Unity requested", never "the view
 * on screen". The What's New badge is deliberately not here either: it lives in accountStore, the
 * one field the board, the Projects home and this page all read.
 */

/** Wire spellings of `CanvasOverlayView` (Assets/Scripts/Popups/CanvasWebOverlayPopupPanel.cs).
 *  20261002 - 'orient': the Shot Editor's coach marks (CanvasOrient.tsx), Unity's
 *  `CanvasOverlayView.Orient`. */
export type CanvasOverlayView = 'menu' | 'scene' | 'shot' | 'preferences' | 'orient';
/** Wire spellings of `CanvasOverlaySource`: which native surface opened the overlay. */
export type CanvasOverlaySource = 'canvas' | 'viewfinder';
/** The four menu rows whose enablement Unity computes (design §4). */
export type CanvasOverlayRowId = 'sceneInfo' | 'shotInfo' | 'export' | 'export3D';

export const CANVAS_OVERLAY_VIEWS: readonly CanvasOverlayView[] = ['menu', 'scene', 'shot', 'preferences', 'orient'];
export const CANVAS_OVERLAY_ROW_IDS: readonly CanvasOverlayRowId[] = ['sceneInfo', 'shotInfo', 'export', 'export3D'];

/**
 * 20261002 - THE SHOT EDITOR COACH MARKS' NINE NATIVE CONTROLS, as Unity names them on the wire
 * (`setOrientTargets`), in the owner's step order. The page cannot see them — they are Unity UI
 * under the transparent overlay — so Unity measures each one and pushes its rectangle here.
 */
export type CanvasOrientTargetId =
  | 'cam' | 'viewer' | 'ar' | 'animatics' | 'viewSwitch' | 'addObject' | 'addShot' | 'script' | 'back';

export const CANVAS_ORIENT_TARGET_IDS: readonly CanvasOrientTargetId[] = [
  'cam', 'viewer', 'ar', 'animatics', 'viewSwitch', 'addObject', 'addShot', 'script', 'back',
];

/**
 * One native control's rectangle, as WINDOW FRACTIONS with a top-left origin (the
 * `setOverlayRegion` convention). NOT clamped to 0..1: an off-screen control reports its true
 * rectangle. `onScreen` is Unity's answer to "can the user see it right now" (the add-shot button
 * on a closed shot list is the one control that arrives applicable but off screen).
 */
export interface CanvasOrientTarget {
  id: CanvasOrientTargetId;
  left: number;
  top: number;
  width: number;
  height: number;
  onScreen: boolean;
}

export interface CanvasOverlayRow {
  id: CanvasOverlayRowId;
  enabled: boolean;
}

/** The touch region of a split window, as WINDOW FRACTIONS (0..1) with a top-left origin. */
export interface CanvasOverlayRegion {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Everything one `showView` carries, already validated by the bridge handler. */
export interface CanvasOverlayShowView {
  view: CanvasOverlayView;
  source: CanvasOverlaySource;
  sceneId: number;
  shotId: number;
  /** The shot's cached preview as a file:// URL, when Unity has one (the slim DB carries none). */
  thumbnailUrl: string | null;
  /** Static image or video on the shot record; null = the host did not say. */
  hasCustomMedia: boolean | null;
}

interface CanvasOverlayState {
  view: CanvasOverlayView | null;
  source: CanvasOverlaySource;
  sceneId: number | null;
  shotId: number | null;
  thumbnailUrl: string | null;
  hasCustomMedia: boolean | null;
  /** Unity's enablement for the four context rows. Empty = not pushed yet = every row enabled. */
  rows: CanvasOverlayRow[];
  /** The fold-split touch region, or null when the window is not split. */
  region: CanvasOverlayRegion | null;
  /**
   * 20261006 - the opener's native top bar (`setOverlayTopBar`): its bottom edge as a fraction of
   * the web view's HEIGHT from the top, or null when no bar is shown or Unity could not measure it.
   * Read only by the macOS docked sheet's top offset (overlayRegion.ts boardShellHeight).
   */
  topBarBottom: number | null;
  /**
   * Bumped by EVERY showView, including one that repeats the last view: the page reacts to the
   * bump, not to a change of `view`, because a warm re-show of the same view must open it again.
   */
  showSeq: number;
  /**
   * 20261002 - the coach marks' native targets, as Unity last pushed them (`setOrientTargets`), or
   * null when none have arrived for this presentation. Unity forces a push on `ready` and on the
   * warm reveal, BOTH of which can land before the `showView {view:'orient'}` they belong to — so a
   * showView for 'orient' keeps whatever is here; any other view, and the end of a page session,
   * clear it.
   */
  orientTargets: CanvasOrientTarget[] | null;
  /** Bumped by every targets push, so a consumer can re-measure when a control MOVED (the shot
   *  list sliding, a rotation) even though the list's ids did not change. */
  orientTargetsSeq: number;

  applyShowView: (show: CanvasOverlayShowView) => void;
  setOverlayContext: (source: CanvasOverlaySource, rows: CanvasOverlayRow[]) => void;
  setOverlayRegion: (region: CanvasOverlayRegion | null) => void;
  setOverlayTopBar: (bottom: number | null) => void;
  setOrientTargets: (targets: CanvasOrientTarget[]) => void;
  clearOrientTargets: () => void;
}

export const useCanvasOverlayStore = create<CanvasOverlayState>((set) => ({
  view: null,
  source: 'canvas',
  sceneId: null,
  shotId: null,
  thumbnailUrl: null,
  hasCustomMedia: null,
  rows: [],
  region: null,
  topBarBottom: null,
  showSeq: 0,
  orientTargets: null,
  orientTargetsSeq: 0,

  applyShowView: (show) => set((s) => ({
    ...show,
    showSeq: s.showSeq + 1,
    ...(show.view === 'orient' ? {} : { orientTargets: null }),
  })),
  setOverlayContext: (source, rows) => set({ source, rows }),
  setOverlayRegion: (region) => set({ region }),
  setOverlayTopBar: (topBarBottom) => set({ topBarBottom }),
  setOrientTargets: (targets) => set((s) => ({ orientTargets: targets, orientTargetsSeq: s.orientTargetsSeq + 1 })),
  clearOrientTargets: () => set({ orientTargets: null }),
}));
